import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  GridLegacy as Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  ViewModule as ModuleIcon,
  Category as CategoryIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  IntegrationInstructions as IntegrationIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ModulePermissionsAdmin: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // State for Permission Groups
  const [permissionGroups, setPermissionGroups] = useState<any[]>([]);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<any>(null);
  const [groupForm, setGroupForm] = useState({ name: '', description: '' });

  // State for Modules
  const [modules, setModules] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [editingModule, setEditingModule] = useState<any>(null);
  const [moduleForm, setModuleForm] = useState({
    module_key: '',
    title: '',
    description: '',
    route_path: '',
    icon_name: '',
    color: '',
    category_id: '',
    is_active: true,
  });

  // State for Category Management
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    display_order: 0,
    icon: '',
  });

  // State for Module Assignments
  const [selectedGroup, setSelectedGroup] = useState<number | null>(null);
  const [assignedModules, setAssignedModules] = useState<number[]>([]);

  // State for Organizations
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<any>(null);
  const [orgForm, setOrgForm] = useState<{ name: string; parent_organization_id: number | '' }>({
    name: '',
    parent_organization_id: '',
  });
  const [orgModulesDialogOpen, setOrgModulesDialogOpen] = useState(false);
  const [selectedOrgForModules, setSelectedOrgForModules] = useState<any>(null);
  const [assignedOrgModules, setAssignedOrgModules] = useState<number[]>([]);

  // State for Organization Integrations
  const [orgIntegrationsDialogOpen, setOrgIntegrationsDialogOpen] = useState(false);
  const [selectedOrgForIntegrations, setSelectedOrgForIntegrations] = useState<any>(null);
  const [integrationForm, setIntegrationForm] = useState({
    provider: 'dialpad',
    target_type: 'department',
    target_id: '',
    api_key_ref: 'DIALPAD_API_KEY',
    is_active: true,
  });
  const [dialpadTargets, setDialpadTargets] = useState<{ departments: any[]; callcenters: any[] } | null>(null);
  const [loadingTargets, setLoadingTargets] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [groupsData, modulesData, categoriesData, orgsData] = await Promise.all([
        api.getAllPermissionGroups(),
        api.getAllModules(),
        api.getAllCategories(),
        api.getOrganizations(),
      ]);
      setPermissionGroups(groupsData);
      setModules(modulesData);
      setCategories(categoriesData);
      setOrganizations(orgsData.organizations || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
    setSuccess(null);
  };

  // ========================================
  // ORGANIZATIONS
  // ========================================

  const handleCreateOrg = () => {
    setEditingOrg(null);
    setOrgForm({ name: '', parent_organization_id: '' });
    setOrgDialogOpen(true);
  };

  const handleEditOrg = (org: any) => {
    setEditingOrg(org);
    setOrgForm({
      name: org.name,
      parent_organization_id: org.parent_organization_id ?? '',
    });
    setOrgDialogOpen(true);
  };

  const handleSaveOrg = async () => {
    if (!orgForm.name.trim()) {
      setError('Organization name is required');
      return;
    }
    try {
      setLoading(true);
      const payload = {
        name: orgForm.name.trim(),
        parentOrganizationId:
          orgForm.parent_organization_id === ''
            ? null
            : Number(orgForm.parent_organization_id),
      };
      if (editingOrg) {
        await api.updateOrganization(editingOrg.id, payload);
        setSuccess('Organization updated successfully');
      } else {
        await api.createOrganization(payload);
        setSuccess('Organization created successfully');
      }
      setOrgDialogOpen(false);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save organization');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrg = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this organization?')) return;
    try {
      setLoading(true);
      await api.deleteOrganization(id);
      setSuccess('Organization deleted successfully');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete organization');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenOrgModules = async (org: any) => {
    try {
      setLoading(true);
      setSelectedOrgForModules(org);
      const data = await api.getOrganizationModules(org.id);
      setAssignedOrgModules(data.module_ids || []);
      setOrgModulesDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load organization modules');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleOrgModule = (moduleId: number) => {
    setAssignedOrgModules((prev) =>
      prev.includes(moduleId)
        ? prev.filter((id) => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleSaveOrgModules = async () => {
    if (!selectedOrgForModules) return;
    try {
      setLoading(true);
      await api.updateOrganizationModules(selectedOrgForModules.id, assignedOrgModules);
      setSuccess(`Module entitlements updated for ${selectedOrgForModules.name}`);
      setOrgModulesDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save organization modules');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenOrgIntegrations = async (org: any) => {
    try {
      setLoading(true);
      setSelectedOrgForIntegrations(org);
      setDialpadTargets(null);
      const data = await api.getOrganizationIntegrations(org.id);
      const dialpad = (data.integrations || []).find((i: any) => i.provider === 'dialpad');
      setIntegrationForm({
        provider: 'dialpad',
        target_type: dialpad?.config?.target_type || 'department',
        target_id: dialpad?.config?.target_id ? String(dialpad.config.target_id) : '',
        api_key_ref: dialpad?.config?.api_key_ref || 'DIALPAD_API_KEY',
        is_active: dialpad ? dialpad.is_active : true,
      });
      setOrgIntegrationsDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load organization integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleBrowseDialpadTargets = async () => {
    try {
      setLoadingTargets(true);
      const targets = await api.getEmbedPhoneTargets();
      setDialpadTargets(targets);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load Dialpad targets');
    } finally {
      setLoadingTargets(false);
    }
  };

  const handleSaveIntegration = async () => {
    if (!selectedOrgForIntegrations) return;
    if (!integrationForm.target_id.trim()) {
      setError('Target ID is required');
      return;
    }
    if (!/^[A-Z_][A-Z0-9_]*$/.test(integrationForm.api_key_ref.trim())) {
      setError('API Key Env Var must be an environment variable name (e.g. DIALPAD_API_KEY), not the API key itself');
      return;
    }
    try {
      setLoading(true);
      await api.upsertOrganizationIntegration(selectedOrgForIntegrations.id, {
        provider: integrationForm.provider,
        config: {
          target_type: integrationForm.target_type,
          target_id: integrationForm.target_id.trim(),
          api_key_ref: integrationForm.api_key_ref.trim() || 'DIALPAD_API_KEY',
        },
        is_active: integrationForm.is_active,
      });
      setSuccess(`Integration saved for ${selectedOrgForIntegrations.name}`);
      setOrgIntegrationsDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save integration');
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // PERMISSION GROUPS
  // ========================================

  const handleCreateGroup = () => {
    setEditingGroup(null);
    setGroupForm({ name: '', description: '' });
    setGroupDialogOpen(true);
  };

  const handleEditGroup = (group: any) => {
    setEditingGroup(group);
    setGroupForm({ name: group.name, description: group.description || '' });
    setGroupDialogOpen(true);
  };

  const handleSaveGroup = async () => {
    try {
      setLoading(true);
      if (editingGroup) {
        await api.updatePermissionGroup(editingGroup.id, groupForm);
        setSuccess('Permission group updated successfully');
      } else {
        await api.createPermissionGroup(groupForm);
        setSuccess('Permission group created successfully');
      }
      setGroupDialogOpen(false);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save permission group');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this permission group?')) return;
    try {
      setLoading(true);
      await api.deletePermissionGroup(id);
      setSuccess('Permission group deleted successfully');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete permission group');
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // MODULES
  // ========================================

  const handleCreateModule = () => {
    setEditingModule(null);
    setModuleForm({
      module_key: '',
      title: '',
      description: '',
      route_path: '',
      icon_name: '',
      color: '',
      category_id: '',
      is_active: true,
    });
    setModuleDialogOpen(true);
  };

  const handleEditModule = (module: any) => {
    setEditingModule(module);
    setModuleForm({
      module_key: module.module_key,
      title: module.title,
      description: module.description || '',
      route_path: module.route_path,
      icon_name: module.icon_name || '',
      color: module.color || '',
      category_id: module.category_id || '',
      is_active: module.is_active,
    });
    setModuleDialogOpen(true);
  };

  const handleSaveModule = async () => {
    try {
      setLoading(true);
      const payload: any = {
        ...moduleForm,
        category_id: moduleForm.category_id ? Number(moduleForm.category_id) : undefined,
      };
      
      // Remove module_key when updating (it shouldn't be changed)
      if (editingModule) {
        delete payload.module_key;
        await api.updateModule(editingModule.id, payload);
        setSuccess('Module updated successfully');
      } else {
        await api.createModule(payload);
        setSuccess('Module created successfully');
      }
      setModuleDialogOpen(false);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save module');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteModule = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this module?')) return;
    try {
      setLoading(true);
      await api.deleteModule(id);
      setSuccess('Module deleted successfully');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete module');
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // CATEGORIES
  // ========================================

  const handleCreateCategory = () => {
    setEditingCategory(null);
    setCategoryForm({ name: '', description: '', display_order: 0, icon: '' });
    setCategoryDialogOpen(true);
  };

  const handleEditCategory = (category: any) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      display_order: category.display_order || 0,
      icon: category.icon || '',
    });
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    try {
      setLoading(true);
      if (editingCategory) {
        await api.updateCategory(editingCategory.id, categoryForm);
        setSuccess('Category updated successfully');
      } else {
        await api.createCategory(categoryForm);
        setSuccess('Category created successfully');
      }
      setCategoryDialogOpen(false);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      setLoading(true);
      await api.deleteCategory(id);
      setSuccess('Category deleted successfully');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete category');
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // MODULE ASSIGNMENTS
  // ========================================

  const handleSelectGroupForAssignment = async (groupId: number) => {
    setSelectedGroup(groupId);
    try {
      setLoading(true);
      const assigned = await api.getModulesForGroup(groupId);
      setAssignedModules(assigned.map((m: any) => m.id));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load assigned modules');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleModuleAssignment = (moduleId: number) => {
    setAssignedModules((prev) =>
      prev.includes(moduleId)
        ? prev.filter((id) => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleSaveAssignments = async () => {
    if (!selectedGroup) return;
    try {
      setLoading(true);
      await api.assignModulesToGroup(selectedGroup, assignedModules);
      setSuccess('Module assignments saved successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save assignments');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Paper elevation={3}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#1976d2', color: 'white' }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            <Typography variant="h5">Module Permissions Administration</Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ m: 2 }}>
            {success}
          </Alert>
        )}

        <Tabs value={tabValue} onChange={handleTabChange} sx={{ px: 2 }}>
          <Tab icon={<SecurityIcon />} label="Permission Groups" />
          <Tab icon={<ModuleIcon />} label="Modules" />
          <Tab icon={<CategoryIcon />} label="Categories" />
          <Tab icon={<AssignmentIcon />} label="Assign Modules" />
          <Tab icon={<BusinessIcon />} label="Organizations" />
        </Tabs>

        {/* PERMISSION GROUPS TAB */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Permission Groups</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateGroup}
            >
              Create Group
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Role Mapping</TableCell>
                  <TableCell>System Group</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permissionGroups.map((group) => (
                  <TableRow key={group.id}>
                    <TableCell>
                      <strong>{group.name}</strong>
                    </TableCell>
                    <TableCell>{group.description}</TableCell>
                    <TableCell>
                      <Chip label={group.legacy_role_mapping || 'None'} size="small" />
                    </TableCell>
                    <TableCell>
                      {group.is_system_group ? (
                        <Chip label="System" color="primary" size="small" />
                      ) : (
                        <Chip label="Custom" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleEditGroup(group)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteGroup(group.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* MODULES TAB */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Registered Modules</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateModule}
            >
              Register Module
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Module Key</TableCell>
                  <TableCell>Route</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modules.map((module) => (
                  <TableRow key={module.id}>
                    <TableCell>
                      <strong>{module.title}</strong>
                    </TableCell>
                    <TableCell>
                      <code>{module.module_key}</code>
                    </TableCell>
                    <TableCell>{module.route_path}</TableCell>
                    <TableCell>
                      {module.category?.name || 'Uncategorized'}
                    </TableCell>
                    <TableCell>
                      {module.is_active ? (
                        <Chip label="Active" color="success" size="small" />
                      ) : (
                        <Chip label="Inactive" color="default" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleEditModule(module)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteModule(module.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* CATEGORIES TAB */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Module Categories</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateCategory}
            >
              Create Category
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Display Order</TableCell>
                  <TableCell>Icon</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((category) => (
                  <TableRow key={category.id}>
                    <TableCell>
                      <strong>{category.name}</strong>
                    </TableCell>
                    <TableCell>{category.description}</TableCell>
                    <TableCell>{category.display_order}</TableCell>
                    <TableCell>{category.icon}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleEditCategory(category)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteCategory(category.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* ASSIGN MODULES TAB */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Select Permission Group
                </Typography>
                {permissionGroups.map((group) => (
                  <Button
                    key={group.id}
                    fullWidth
                    variant={selectedGroup === group.id ? 'contained' : 'outlined'}
                    onClick={() => handleSelectGroupForAssignment(group.id)}
                    sx={{ mb: 1, justifyContent: 'flex-start' }}
                  >
                    {group.name}
                  </Button>
                ))}
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              {selectedGroup ? (
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">
                      Assign Modules to{' '}
                      {permissionGroups.find((g) => g.id === selectedGroup)?.name}
                    </Typography>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveAssignments}
                      disabled={loading}
                    >
                      Save Assignments
                    </Button>
                  </Box>
                  {categories.map((category) => {
                    const categoryModules = modules.filter(
                      (m) => m.category_id === category.id
                    );
                    if (categoryModules.length === 0) return null;
                    return (
                      <Box key={category.id} sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {category.name}
                        </Typography>
                        {categoryModules.map((module) => (
                          <FormControlLabel
                            key={module.id}
                            control={
                              <Checkbox
                                checked={assignedModules.includes(module.id)}
                                onChange={() => handleToggleModuleAssignment(module.id)}
                              />
                            }
                            label={`${module.title} (${module.module_key})`}
                            sx={{ display: 'block', ml: 2 }}
                          />
                        ))}
                      </Box>
                    );
                  })}
                </Paper>
              ) : (
                <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    Select a permission group to manage module assignments
                  </Typography>
                </Paper>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* ORGANIZATIONS TAB */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Organizations</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateOrg}
            >
              Add Organization
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Parent Organization</TableCell>
                  <TableCell>Users</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {organizations.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell>
                      <strong>{org.name}</strong>
                    </TableCell>
                    <TableCell>
                      {org.parent_name ? (
                        <Chip label={org.parent_name} size="small" />
                      ) : (
                        <Chip label="Top-Level" color="primary" size="small" />
                      )}
                    </TableCell>
                    <TableCell>{org.user_count}</TableCell>
                    <TableCell>
                      {org.created_at ? new Date(org.created_at).toLocaleDateString() : '\u2014'}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenOrgModules(org)}
                        title="Manage module entitlements"
                      >
                        <ModuleIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => handleOpenOrgIntegrations(org)}
                        title="Manage integrations"
                      >
                        <IntegrationIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleEditOrg(org)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteOrg(org.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>

      {/* GROUP DIALOG */}
      <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGroup ? 'Edit Permission Group' : 'Create Permission Group'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={groupForm.name}
            onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={groupForm.description}
            onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialogOpen(false)} startIcon={<CloseIcon />}>
            Cancel
          </Button>
          <Button onClick={handleSaveGroup} variant="contained" startIcon={<SaveIcon />}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODULE DIALOG */}
      <Dialog open={moduleDialogOpen} onClose={() => setModuleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingModule ? 'Edit Module' : 'Register New Module'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Module Key"
                value={moduleForm.module_key}
                onChange={(e) => setModuleForm({ ...moduleForm, module_key: e.target.value })}
                required
                disabled={!!editingModule}
                helperText="Unique identifier (e.g., 'timeclock-admin')"
              />
              <TextField
                fullWidth
                label="Title"
                value={moduleForm.title}
                onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                required
              />
            </Box>
            <TextField
              fullWidth
              label="Description"
              value={moduleForm.description}
              onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
              multiline
              rows={2}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Route Path"
                value={moduleForm.route_path}
                onChange={(e) => setModuleForm({ ...moduleForm, route_path: e.target.value })}
                required
                helperText="e.g., '/timeclock-admin'"
              />
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={moduleForm.category_id}
                  onChange={(e) => setModuleForm({ ...moduleForm, category_id: e.target.value })}
                  label="Category"
                >
                  <MenuItem value="">None</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Icon Name"
                value={moduleForm.icon_name}
                onChange={(e) => setModuleForm({ ...moduleForm, icon_name: e.target.value })}
                helperText="MUI icon name"
              />
              <TextField
                fullWidth
                label="Color"
                value={moduleForm.color}
                onChange={(e) => setModuleForm({ ...moduleForm, color: e.target.value })}
                helperText="e.g., '#1976d2'"
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={moduleForm.is_active}
                  onChange={(e) => setModuleForm({ ...moduleForm, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModuleDialogOpen(false)} startIcon={<CloseIcon />}>
            Cancel
          </Button>
          <Button onClick={handleSaveModule} variant="contained" startIcon={<SaveIcon />}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* CATEGORY DIALOG */}
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edit Category' : 'Create Category'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={categoryForm.name}
            onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={categoryForm.description}
            onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="Display Order"
            type="number"
            value={categoryForm.display_order}
            onChange={(e) => setCategoryForm({ ...categoryForm, display_order: parseInt(e.target.value) || 0 })}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Icon"
            value={categoryForm.icon}
            onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
            margin="normal"
            helperText="MUI icon name"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)} startIcon={<CloseIcon />}>
            Cancel
          </Button>
          <Button onClick={handleSaveCategory} variant="contained" startIcon={<SaveIcon />}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ORGANIZATION DIALOG */}
      <Dialog open={orgDialogOpen} onClose={() => setOrgDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingOrg ? 'Edit Organization' : 'Add Organization'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={orgForm.name}
            onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
            margin="normal"
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Parent Organization</InputLabel>
            <Select
              value={orgForm.parent_organization_id}
              onChange={(e) =>
                setOrgForm({
                  ...orgForm,
                  parent_organization_id: e.target.value as number | '',
                })
              }
              label="Parent Organization"
            >
              <MenuItem value="">None (Top-Level)</MenuItem>
              {organizations
                .filter((org) => !editingOrg || org.id !== editingOrg.id)
                .map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrgDialogOpen(false)} startIcon={<CloseIcon />}>
            Cancel
          </Button>
          <Button onClick={handleSaveOrg} variant="contained" startIcon={<SaveIcon />}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ORG MODULE ENTITLEMENTS DIALOG */}
      <Dialog
        open={orgModulesDialogOpen}
        onClose={() => setOrgModulesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Module Entitlements: {selectedOrgForModules?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which modules this organization is entitled to. Users still
            need the module granted through their permission group.
          </Typography>
          {categories.map((category) => {
            const categoryModules = modules.filter(
              (m) => m.category_id === category.id
            );
            if (categoryModules.length === 0) return null;
            return (
              <Box key={category.id} sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {category.name}
                </Typography>
                {categoryModules.map((module) => (
                  <FormControlLabel
                    key={module.id}
                    control={
                      <Checkbox
                        checked={assignedOrgModules.includes(module.id)}
                        onChange={() => handleToggleOrgModule(module.id)}
                      />
                    }
                    label={`${module.title} (${module.module_key})`}
                    sx={{ display: 'block', ml: 2 }}
                  />
                ))}
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrgModulesDialogOpen(false)} startIcon={<CloseIcon />}>
            Cancel
          </Button>
          <Button onClick={handleSaveOrgModules} variant="contained" startIcon={<SaveIcon />}>
            Save Entitlements
          </Button>
        </DialogActions>
      </Dialog>

      {/* ORG INTEGRATIONS DIALOG */}
      <Dialog
        open={orgIntegrationsDialogOpen}
        onClose={() => setOrgIntegrationsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Integrations: {selectedOrgForIntegrations?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure third-party integrations for this organization. API keys
            live in server environment variables - only the env var NAME is
            stored here.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Provider</InputLabel>
              <Select
                value={integrationForm.provider}
                onChange={(e) => setIntegrationForm({ ...integrationForm, provider: e.target.value })}
                label="Provider"
              >
                <MenuItem value="dialpad">Dialpad</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Target Type</InputLabel>
              <Select
                value={integrationForm.target_type}
                onChange={(e) => setIntegrationForm({ ...integrationForm, target_type: e.target.value })}
                label="Target Type"
              >
                <MenuItem value="department">Department</MenuItem>
                <MenuItem value="callcenter">Call Center</MenuItem>
                <MenuItem value="office">Office</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                label="Target ID"
                value={integrationForm.target_id}
                onChange={(e) => setIntegrationForm({ ...integrationForm, target_id: e.target.value })}
                required
                helperText="Dialpad department/call center ID"
              />
              <Button
                variant="outlined"
                onClick={handleBrowseDialpadTargets}
                disabled={loadingTargets}
                sx={{ mt: 1, whiteSpace: 'nowrap' }}
              >
                {loadingTargets ? 'Loading...' : 'Browse Dialpad'}
              </Button>
            </Box>
            {dialpadTargets && (
              <FormControl fullWidth>
                <InputLabel>Select from Dialpad</InputLabel>
                <Select
                  value=""
                  onChange={(e) => {
                    const [type, id] = String(e.target.value).split('|');
                    setIntegrationForm({ ...integrationForm, target_type: type, target_id: id });
                  }}
                  label="Select from Dialpad"
                >
                  {dialpadTargets.departments.map((d: any) => (
                    <MenuItem key={`department-${d.id}`} value={`department|${d.id}`}>
                      Department: {d.name}
                    </MenuItem>
                  ))}
                  {dialpadTargets.callcenters.map((c: any) => (
                    <MenuItem key={`callcenter-${c.id}`} value={`callcenter|${c.id}`}>
                      Call Center: {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              fullWidth
              label="API Key Env Var"
              value={integrationForm.api_key_ref}
              onChange={(e) => setIntegrationForm({ ...integrationForm, api_key_ref: e.target.value })}
              helperText="Name of the server environment variable holding the Dialpad API key"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={integrationForm.is_active}
                  onChange={(e) => setIntegrationForm({ ...integrationForm, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrgIntegrationsDialogOpen(false)} startIcon={<CloseIcon />}>
            Cancel
          </Button>
          <Button onClick={handleSaveIntegration} variant="contained" startIcon={<SaveIcon />}>
            Save Integration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModulePermissionsAdmin;
